import React, { useState, useCallback } from 'react'
import { Button, Flex, Tooltip, Typography, message, Divider } from 'antd'
import { PaperClipOutlined, EyeOutlined, ExportOutlined, DownloadOutlined } from '@ant-design/icons'
import fileDownload from 'js-file-download'
import { useFetchSubmissionAttachmentsQuery, useLazyDownloadAttachmentQuery } from '../../../../../../../../store/apis/submission.api'
import { getAttachmentKind, getKindFromMimeType, getMimeType } from '../../../../../../../../utils/attachment.helper'
import { useThemeColors } from '../../../../../../../../hooks/useThemeColors'
import AttachmentPreviewModal from './AttachmentPreviewModal'

const NEW_TAB_REVOKE_DELAY_MS = 60_000

// loadingAction shape: { id, action: 'preview' | 'tab' | 'download' } | null
const isActionLoading = (loadingAction, id, action) =>
    loadingAction?.id === id && loadingAction?.action === action

const SubmissionAttachments = ({ submissionId, questionId }) => {
    const { data: attachments } = useFetchSubmissionAttachmentsQuery({ submissionId })
    const [downloadAttachment] = useLazyDownloadAttachmentQuery()
    const colors = useThemeColors()

    const [loadingAction, setLoadingAction] = useState(null)
    const [preview, setPreview] = useState(null) // { objectUrl, blob, kind, name }

    const questionAttachments = attachments?.filter(attachment =>
        questionId ? attachment.questionId === questionId : true
    ) || []

    /**
     * Fetches the blob for an attachment, wraps it with the correct MIME type,
     * and returns { objectUrl, blob, mimeType, kind }.
     * Content-Type from the response header is used as the primary source;
     * filename extension is the fallback (covers cases where the header is
     * absent or generic application/octet-stream).
     */
    const fetchBlobUrl = useCallback(async (attachmentId, name) => {
        const response = await downloadAttachment({ attachmentId })
        if (!('data' in response)) throw new Error('Failed to fetch attachment')
        const { blob: rawBlob, contentType } = response.data
        // Trust the backend Content-Type directly; fall back to filename only if header is absent.
        const mimeType = contentType
            ? contentType.split(';')[0].trim()
            : getMimeType(name)
        const blob = new Blob([rawBlob], { type: mimeType })
        const objectUrl = URL.createObjectURL(blob)
        const kind = getKindFromMimeType(mimeType) ?? getAttachmentKind(name)
        return { objectUrl, blob, kind }
    }, [downloadAttachment])

    const handlePreview = async (attachment) => {
        setLoadingAction({ id: attachment.id, action: 'preview' })
        try {
            const { objectUrl, blob, kind } = await fetchBlobUrl(attachment.id, attachment.name)
            if (kind === 'other') {
                // File was not converted (e.g. legacy .docx without a PDF conversion).
                URL.revokeObjectURL(objectUrl)
                message.warning('This file cannot be previewed. Please use the download button instead.')
                return
            }
            setPreview({ objectUrl, blob, kind, name: attachment.name })
        } catch (error) {
            console.error('Failed to preview attachment:', error)
            message.error('Could not load attachment for preview.')
        } finally {
            setLoadingAction(null)
        }
    }

    const handleOpenInNewTab = async (attachment) => {
        setLoadingAction({ id: attachment.id, action: 'tab' })
        try {
            const { objectUrl, kind } = await fetchBlobUrl(attachment.id, attachment.name)
            if (kind === 'other') {
                // File was not converted (e.g. legacy .docx without a PDF conversion).
                URL.revokeObjectURL(objectUrl)
                message.warning('This file cannot be previewed. Please use the download button instead.')
                return
            }
            const tab = window.open(objectUrl, '_blank')
            if (!tab) {
                // Popup blocker prevented the new tab from opening
                URL.revokeObjectURL(objectUrl)
                message.warning('Pop-up blocked. Please allow pop-ups for this site and try again.')
            } else {
                setTimeout(() => URL.revokeObjectURL(objectUrl), NEW_TAB_REVOKE_DELAY_MS)
            }
        } catch (error) {
            console.error('Failed to open attachment in new tab:', error)
            message.error('Could not open attachment in new tab.')
        } finally {
            setLoadingAction(null)
        }
    }

    const handleDownload = async (attachment) => {
        setLoadingAction({ id: attachment.id, action: 'download' })
        try {
            const response = await downloadAttachment({ attachmentId: attachment.id, original: true })
            if ('data' in response) fileDownload(response.data.blob, attachment.name)
            else message.error('Could not download attachment.')
        } catch (error) {
            console.error('Failed to download attachment:', error)
            message.error('Could not download attachment.')
        } finally {
            setLoadingAction(null)
        }
    }

    const handleClosePreview = () => {
        if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl)
        setPreview(null)
    }

    if (questionAttachments.length === 0) return null

    const anyLoading = loadingAction !== null

    const pillStyle = {
        border: `1px solid ${colors.borderColor}`,
        borderRadius: 6,
        padding: '4px 8px',
        backgroundColor: colors.nestedCardBg,
    }

    const dividerStyle = { margin: '0 4px', height: '14px', borderColor: colors.borderColor }

    return (
        <>
            <Flex vertical gap={6}>
                {questionAttachments.map(attachment => {
                    const kind = getAttachmentKind(attachment.name)
                    const canPreview = kind === 'image' || kind === 'pdf'

                    return (
                        <Flex key={attachment.id} align='center' gap={0} wrap='nowrap' style={pillStyle}>
                            <PaperClipOutlined style={{ flexShrink: 0, color: colors.secondaryText, marginRight: 6 }} />
                            <Tooltip title={attachment.name} placement='topLeft'>
                                <Typography.Text
                                    ellipsis
                                    style={{ maxWidth: 200, fontSize: 13, flexShrink: 1, color: colors.text }}
                                >
                                    {attachment.name}
                                </Typography.Text>
                            </Tooltip>
                            <Divider type='vertical' style={{ ...dividerStyle, marginLeft: 8 }} />
                            <Flex gap={0} style={{ flexShrink: 0 }}>
                                {canPreview && (
                                    <Tooltip title='Preview'>
                                        <Button
                                            type='text'
                                            size='small'
                                            icon={<EyeOutlined />}
                                            loading={isActionLoading(loadingAction, attachment.id, 'preview')}
                                            disabled={anyLoading}
                                            onClick={() => handlePreview(attachment)}
                                        />
                                    </Tooltip>
                                )}
                                {canPreview && (
                                    <Tooltip title='Open in new tab'>
                                        <Button
                                            type='text'
                                            size='small'
                                            icon={<ExportOutlined />}
                                            loading={isActionLoading(loadingAction, attachment.id, 'tab')}
                                            disabled={anyLoading}
                                            onClick={() => handleOpenInNewTab(attachment)}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip title='Download'>
                                    <Button
                                        type='text'
                                        size='small'
                                        icon={<DownloadOutlined />}
                                        loading={isActionLoading(loadingAction, attachment.id, 'download')}
                                        disabled={anyLoading}
                                        onClick={() => handleDownload(attachment)}
                                    />
                                </Tooltip>
                            </Flex>
                        </Flex>
                    )
                })}
            </Flex>

            {preview && (
                <AttachmentPreviewModal
                    open
                    onClose={handleClosePreview}
                    kind={preview.kind}
                    objectUrl={preview.objectUrl}
                    blob={preview.blob}
                    name={preview.name}
                />
            )}
        </>
    )
}

export default SubmissionAttachments
